import {
  LoadingOutlined,
  PauseOutlined,
  CaretRightOutlined,
} from '@ant-design/icons';
import cn from 'classnames';
import { useEffect, useRef, useState } from 'react';

import { useLeadCardContext } from '@app/components/LeadCard/context';
import { LeadAudioWave } from '@app/components/LeadCard/ui/MessageHistory/components/LeadAudioPlayer/ui/LeadAudioWave';
import { formatLeadAudioTime } from '@app/components/LeadCard/ui/MessageHistory/lib';
import {
  leadAudioPlayback,
  useLeadAudioPlayback,
} from '@app/components/LeadCard/ui/MessageHistory/model/leadAudioPlayback';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';

import type { FC, ReactNode } from 'react';

import './LeadAudioPlayer.scss';

interface LeadAudioPlayerProps {
  fileId: number;
  messageId: number | null;
  title: string;
  isIncoming: boolean;
  // Длительность из данных сообщения. Может быть 0 — тогда точную возьмём из
  // самого файла после загрузки, и волна перерисуется под неё.
  durationSec: number;
  // Файл скачивается по требованию: до первого запуска он не нужен.
  onRequestUrl(): Promise<string | null>;
  // Дополнительные кнопки справа от волны (скачивание, позже транскрипция).
  actions?: ReactNode;
}

export const LeadAudioPlayer: FC<LeadAudioPlayerProps> = ({
  fileId,
  messageId,
  title,
  isIncoming,
  durationSec,
  onRequestUrl,
  actions,
}) => {
  // Лид берём из контекста, а не пропсом: он нужен только стору, чтобы при
  // закрытии одной истории не глушить запись, запущенную из другой.
  const { selectedLeadId } = useLeadCardContext();
  const { showNotification } = useNotification();
  const playback = useLeadAudioPlayback();
  const [isLoading, setIsLoading] = useState(false);

  const isCurrent = playback.fileId === fileId;
  const isPlaying = isCurrent && playback.isPlaying;
  const effectiveDuration =
    isCurrent && playback.durationSec > 0 ? playback.durationSec : durationSec;
  const positionSec = isCurrent ? playback.positionSec : 0;
  const progress =
    effectiveDuration > 0 ? Math.min(1, positionSec / effectiveDuration) : 0;

  // Ошибка декодирования приходит событием уже после того, как play()
  // завершился успехом, поэтому сообщаем о ней по счётчику из стора — и
  // только владелец текущей записи, чтобы уведомление не задвоилось.
  const { errorCount } = playback;
  const reportedErrorCountRef = useRef(errorCount);

  useEffect(() => {
    if (!isCurrent || errorCount === reportedErrorCountRef.current) return;

    reportedErrorCountRef.current = errorCount;
    showNotification('Не удалось воспроизвести запись', 'frontWarning');
  }, [errorCount, isCurrent, showNotification]);

  const playClickHandler = async () => {
    if (isPlaying) {
      leadAudioPlayback.pause();

      return;
    }

    setIsLoading(true);

    try {
      const url = await onRequestUrl();
      if (!url) return;

      await leadAudioPlayback.play({
        fileId,
        leadId: selectedLeadId,
        messageId,
        title,
        isIncoming,
        url,
        durationSec,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const seekHandler = (ratio: number) => {
    if (!isCurrent) return;

    leadAudioPlayback.seekRatio(ratio);
  };

  return (
    <div
      className={cn('LeadAudioPlayer', {
        'LeadAudioPlayer--out': !isIncoming,
      })}
    >
      <button
        type="button"
        className="LeadAudioPlayer__play"
        aria-label={isPlaying ? 'Пауза' : 'Воспроизвести'}
        disabled={isLoading}
        onClick={() => void playClickHandler()}
      >
        {isLoading ? (
          <LoadingOutlined />
        ) : isPlaying ? (
          <PauseOutlined />
        ) : (
          <CaretRightOutlined />
        )}
      </button>
      <LeadAudioWave
        durationSec={effectiveDuration}
        progress={progress}
        isIncoming={isIncoming}
        onSeek={seekHandler}
      />
      <span className="LeadAudioPlayer__time">
        {formatLeadAudioTime(isCurrent ? positionSec : effectiveDuration)}
      </span>
      {actions && <div className="LeadAudioPlayer__actions">{actions}</div>}
    </div>
  );
};
